"use client";

import { ReactNode, useEffect, useRef, useState } from "react";
import type { Selection } from "@react-types/shared";

import { useIsClosing } from "../../hooks/useIsClosing";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { handleInternalNavigation, isUrlExternal } from "../../utils";

import { Button } from "../Button/Button";
import { ScreenOverlay } from "../ScreenOverlay/ScreenOverlay";
import { DropdownMenu } from "radix-ui";
import { Icon } from "../Icon/Icon";

import "./ActionMenu.css";

export interface ActionMenuAction {
  key: string;
  label?: ReactNode;
  description?: ReactNode;
  to?: string;
  onAction?: (key: string) => void;
  children?: ActionMenuAction[];
}

export type ActionMenuSelectionMode = "single" | "multiple" | "none";

export interface ActionMenuProps {
  /** The actions of the menu
   * - type {@link ActionMenuAction}[]
   */
  actions?: ActionMenuAction[];

  /** The text of the cancel button */
  cancelButtonText?: string;

  /** The children of the menu */
  children?: ReactNode | ((props: { close: () => void }) => ReactNode);

  /** The test id of the menu */
  "data-testid"?: string;

  /** The keys of the disabled items
   * @default []
   */
  disabledKeys?: string[];

  /** Whether the menu is open
   * @default false
   */
  isOpen: boolean;

  /** The callback function to close the menu */
  onClose?: () => void;

  /** The callback function to change the selection
   * - type {@link Selection}
   */
  onSelectionChange?: (keys: Selection) => void;

  /** The selection mode of the menu
   * @default "single"
   */
  selectionMode?: ActionMenuSelectionMode;

  /** The keys of the selected items */
  selectedKeys?: string[];

  /** Whether to show the cancel button */
  showCancel?: boolean;

  /** The title of the menu */
  title?: string;
}

type ActionStack = {
  title: ReactNode | null;
  actions: ActionMenuAction[];
  key: string | null;
  previousKey: string | null;
};

/**
 * A controlled ActionMenu to display a menu of actions.
 * Renders a list of actions as a focusable menu, or non-focusable children.
 *
 * API:
 *  - {@link ActionMenuProps}
 */
export const ActionMenu = ({
  isOpen,
  actions = [],
  children,
  showCancel = true,
  cancelButtonText = "Cancel",
  selectionMode = "single",
  selectedKeys,
  disabledKeys = [],
  onSelectionChange,
  onClose,
  title,
  "data-testid": testId,
}: ActionMenuProps) => {
  useLockBodyScroll(isOpen);
  const [contentHeight, setContentHeight] = useState<number>(0);
  const [stackHistory, setStackHistory] = useState<ActionStack[]>([]);
  const [currentActionStack, setCurrentActionStack] = useState<ActionStack>({
    title,
    actions,
    key: null,
    previousKey: null,
  });
  const contentRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const { isClosing, handleClose } = useIsClosing({ onClose, overlayRef });

  const currentActions = currentActionStack.actions || [];
  const hasActions = currentActions && currentActions.length > 0;
  const hasContent = children || hasActions || showCancel;
  const isInSubmenu = stackHistory.length > 0;

  function handleSubmenuOpen(key: string) {
    const action = currentActions.find((action) => action.key === key);
    if (!action) {
      console.error(`Action with key ${key} not found`);
      return;
    }

    setStackHistory((prevStackHistory) => [
      ...prevStackHistory,
      currentActionStack,
    ]);
    setCurrentActionStack((prevStack) => ({
      title: action.label,
      actions: action.children,
      key: action.key,
      previousKey: prevStack.key,
    }));
  }

  function handleBack() {
    if (stackHistory.length > 0) {
      const previousStack = stackHistory[stackHistory.length - 1];
      setCurrentActionStack(previousStack);
      setStackHistory(stackHistory.slice(0, -1));
    }
  }

  // Re-measure the open menu's content height when stack history changes
  useEffect(() => {
    if (!isOpen) return;

    requestAnimationFrame(() => {
      if (contentRef.current) {
        setContentHeight(contentRef.current.scrollHeight + 12);
      }
    });
  }, [isOpen, stackHistory, hasContent]);

  // Reset content height when menu closes or is closing
  useEffect(() => {
    if (!isOpen || isClosing) {
      setContentHeight(0);
    }
  }, [isOpen, isClosing]);

  if (!isOpen && !isClosing) return null;

  return (
    <ScreenOverlay fadeIn ref={overlayRef}>
      <DropdownMenu.Root
        modal
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) handleClose();
        }}
      >
        <DropdownMenu.Trigger asChild>
          {/* No visible trigger, menu is controlled by isOpen */}
          <div>{""}</div>
        </DropdownMenu.Trigger>

        <div
          className="proton-ActionMenu__background-wrapper"
          style={{ opacity: isClosing ? 0 : 1 }}
        >
          <div
            data-testid={testId || "ActionMenu-wrapper"}
            className="proton-ActionMenu__wrapper"
          >
            <div
              className="proton-ActionMenu__card"
              data-testid="ActionMenu-content"
              style={{ height: `${contentHeight}px` }}
            >
              <div ref={menuRef} className="proton-ActionMenu__menu">
                <DropdownMenu.Content loop sideOffset={8} ref={contentRef}>
                  {hasContent && (
                    <>
                      {children && (
                        <div className="proton-ActionMenu__content">
                          {typeof children === "function"
                            ? children({ close: handleClose })
                            : children}
                        </div>
                      )}

                      {hasActions && (
                        <ActionMenuList
                          actions={currentActions}
                          selectionMode={selectionMode}
                          selectedKeys={selectedKeys}
                          onSelectionChange={onSelectionChange}
                          disabledKeys={disabledKeys}
                          handleSubmenuOpen={handleSubmenuOpen}
                          title={currentActionStack.title}
                          isInSubmenu={isInSubmenu}
                          handleBack={handleBack}
                          previousMenuTitle={
                            stackHistory[stackHistory.length - 1]?.title
                          }
                        />
                      )}

                      {showCancel && (
                        <>
                          <DropdownMenu.Separator />
                          <DropdownMenu.Item className="proton-ActionMenu__cancel-button">
                            <Button
                              data-testid="ActionMenuItem-cancel"
                              onPress={handleClose}
                              fullWidth
                              variant="secondary"
                            >
                              {cancelButtonText}
                            </Button>
                          </DropdownMenu.Item>
                        </>
                      )}
                    </>
                  )}
                </DropdownMenu.Content>
              </div>
            </div>
          </div>
        </div>
      </DropdownMenu.Root>
    </ScreenOverlay>
  );
};

interface ActionMenuListProps {
  actions: ActionMenuAction[];
  selectionMode: ActionMenuSelectionMode;
  selectedKeys?: string[];
  onSelectionChange?: (keys: Selection) => void;
  disabledKeys?: string[];
  handleSubmenuOpen: (key: string) => void;
  title?: ReactNode;
  isInSubmenu?: boolean;
  handleBack?: () => void;
  previousMenuTitle?: ReactNode;
}

const ActionMenuList = ({
  actions,
  selectionMode,
  selectedKeys = [],
  onSelectionChange,
  disabledKeys = [],
  handleSubmenuOpen,
  title,
  isInSubmenu = false,
  handleBack,
  previousMenuTitle,
}: ActionMenuListProps) => {
  const ActionMenuItemMap = () => (
    <>
      {isInSubmenu && handleBack && (
        <div className="proton-ActionMenu__back-button-container">
          <DropdownMenu.Item
            className="proton-ActionMenu__back-button"
            onSelect={(event) => {
              event.preventDefault();
              handleBack();
            }}
            aria-label={`Go back to ${previousMenuTitle || "previous menu"}`}
          >
            <Icon id="chevron-left" size={16} aria-hidden="true" />
          </DropdownMenu.Item>
          <div
            className="proton-ActionMenu__title"
            role="banner"
            aria-label={isInSubmenu ? `Submenu: ${title}` : `Menu: ${title}`}
          >
            <span>{title}</span>
          </div>
          <DropdownMenu.Separator />
        </div>
      )}

      {actions.map((action, i) => {
        const itemProps = actions[i];
        return (
          <ActionMenuItem
            key={action.key}
            item={action}
            isSubmenu={Boolean(itemProps.children?.length)}
            selectionMode={selectionMode}
            onSelectionChange={onSelectionChange}
            selectedKeys={selectedKeys}
            disabledKeys={disabledKeys}
            handleSubmenuOpen={handleSubmenuOpen}
          />
        );
      })}
    </>
  );

  return (
    <div
      aria-disabled={selectionMode === "none"}
      className="proton-ActionMenu__list"
      role="menu"
      aria-label={
        title ? `${isInSubmenu ? "Submenu" : "Menu"}: ${title}` : "Action menu"
      }
      aria-orientation="vertical"
    >
      {selectionMode === "single" ? (
        <DropdownMenu.RadioGroup
          value={selectedKeys?.[0] || ""}
          onValueChange={(value) => {
            // Check if this is a submenu item - if so, don't trigger selection change
            const action = actions.find((action) => action.key === value);
            if (action?.children?.length) {
              return;
            }

            if (onSelectionChange) {
              onSelectionChange(new Set([value]));
            }
          }}
        >
          <ActionMenuItemMap />
        </DropdownMenu.RadioGroup>
      ) : (
        <DropdownMenu.Group>
          <ActionMenuItemMap />
        </DropdownMenu.Group>
      )}
    </div>
  );
};

interface ActionMenuItemProps {
  to?: string;
  item: ActionMenuAction;
  isSubmenu?: boolean;
  selectionMode: ActionMenuSelectionMode;
  onSelectionChange?: (keys: Selection) => void;
  selectedKeys?: string[];
  disabledKeys?: string[];
  handleSubmenuOpen: (key: string) => void;
}

const ActionMenuItem = ({
  item,
  isSubmenu,
  selectionMode,
  onSelectionChange,
  selectedKeys = [],
  disabledKeys = [],
  handleSubmenuOpen,
}: ActionMenuItemProps) => {
  const ref = useRef(null);
  const isExternal = item.to && isUrlExternal(item.to);
  const isDisabled = disabledKeys.includes(item.key);
  const isSelected = selectedKeys.includes(item.key);

  const radixItemProps = {
    className: "proton-ActionMenu__item",
    "aria-label": item.label
      ? `${item.label}${item.description ? `, ${item.description}` : ""}`
      : `ActionMenu-Item-${item.key}`,
    role: "menuitem",
    disabled: isDisabled,
    ref,
  };

  const onSelect = (event: Event) => {
    if (item?.children?.length) {
      event.preventDefault();
      handleSubmenuOpen(item.key);
      return;
    }
    if (item.onAction) {
      item.onAction(item.key);
    }
  };

  const content = (
    <>
      <div className="proton-ActionMenu__item-content">
        <div className="proton-ActionMenu__item-label">{item.label}</div>
        {item.description && (
          <div className="proton-ActionMenu__description">
            {item.description}
          </div>
        )}
      </div>
      {isSubmenu && <Icon id="chevron-right" size={16} aria-hidden="true" />}
    </>
  );

  if (item.to) {
    return (
      <DropdownMenu.Item {...radixItemProps} key={item.key} asChild>
        <a
          aria-label={
            item.label
              ? `${item.label}${item.description ? `, ${item.description}` : ""}`
              : `ActionMenu-Item-${item.key}`
          }
          aria-disabled={isDisabled}
          aria-checked={isSelected}
          aria-expanded={isSubmenu ? false : undefined}
          href={item.to}
          target={isExternal ? "_blank" : undefined}
          rel={isExternal ? "noopener noreferrer" : undefined}
          onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
            if (isExternal || !item.to) {
              return;
            }
            handleInternalNavigation(e, item.to);
          }}
        >
          {content}
        </a>
      </DropdownMenu.Item>
    );
  }

  if (selectionMode === "multiple") {
    return (
      <DropdownMenu.CheckboxItem
        {...radixItemProps}
        key={item.key}
        checked={isSelected}
        onSelect={onSelect}
        onCheckedChange={(checked: boolean) => {
          if (!onSelectionChange) return;

          const currentKeys = new Set(selectedKeys);
          if (checked) {
            currentKeys.add(item.key);
          } else {
            currentKeys.delete(item.key);
          }
          onSelectionChange(currentKeys);
        }}
      >
        {content}
      </DropdownMenu.CheckboxItem>
    );
  }

  return (
    <DropdownMenu.RadioItem
      {...radixItemProps}
      value={item.key}
      key={item.key}
      aria-checked={isSelected}
      aria-expanded={isSubmenu ? false : undefined}
      onSelect={onSelect}
    >
      {content}
    </DropdownMenu.RadioItem>
  );
};
